import React, { useState } from 'react'
import {
  BoxV2,
  ButtonV2,
  ColumnActionWithMenuColors,
  ColumnActionWithMenuV2,
  ColumnMenuOption,
  VerticalDivider,
} from '@labrav/react-components'
import { useStyles } from './CollapesBox.styles'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPencil, faPlus } from '@fortawesome/pro-regular-svg-icons'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from '../../../../store'
import { StorefrontState } from '../../../../modules/storefront/types'
import { ListingType } from '../../../../modules/storefront/listingCardCreation/types'
import button from '../../../../common/images/button.svg'
import listingCard from '../../../../common/images/listingCard.svg'
import { getCloudImg } from '../../../../common/utils/getCloudImg'
import { Typography } from '@mui/material'
import { NoDataView } from './NoDataView/NoDataView'
import { clearButtonData } from '../../../../modules/storefront/buttonCreation/action'
import { clearAutosaveStatus, useAccountId } from '@labrav/common'
import { clearListingCardData } from '../../../../modules/storefront/listingCardCreation/action'
import { deleteSelectedIntegration } from '../../../../modules/storefront/action'
import { EmbedCodeModal } from '../EmbedCodeModal/EmbedCodeModal'
import { DeleteConfirmationModal } from './DeleteConfirmationModal/DeleteConfirmationModal'
import { INTEGRATION_MENU_KEYS } from '../../constants'
import {
  getCreatePath,
  getEmptyStateCopy,
  getIntegrationsByType,
  getIntegrationTypeLabel,
  isEmbedMenuDisabled,
} from '../../helpers'

type CollapesBoxProps = {
  handleClick?: (id: string) => () => void
  handleThreeDot?: () => void
  type?: ListingType
  title?: string
}

export const CollapesBox: React.FC<CollapesBoxProps> = ({
  handleClick,
  type,
  title,
}) => {
  const dispatch = useDispatch()
  const { classes } = useStyles()
  const navigate = useNavigate()
  const { integrations } = useSelector<StorefrontState>(
    state => state.storefrontData
  )
  const accountId = useAccountId({ product: 'flyout' })

  const [hoveredRow, setHoveredRow] = useState<string | null>(null)
  const [openlModal, setOpenModal] = useState(false)
  const [openDeleteModal, setOpenDeleteModal] = useState(false)
  const [selectedIntegrationId, setSelectedIntegrationId] = useState<
    string | null
  >(null)
  const [selectedIntegrationType, setSelectedIntegrationType] = useState<
    string | null
  >(null)

  const handleCloseModal = () => {
    setOpenModal(false)
    setSelectedIntegrationId(null)
  }
  const handleOpenModal = () => {
    setOpenModal(true)
  }

  const handleOpenDeleteModal = () => {
    setOpenDeleteModal(true)
  }
  const handleCloseDeleteModal = () => {
    setOpenDeleteModal(false)
  }

  const handleDeleteModal = async () => {
    if (selectedIntegrationId) {
      await dispatch(deleteSelectedIntegration(selectedIntegrationId))
    }
    handleCloseDeleteModal()
  }

  const navigateToCreate = async () => {
    if (type === ListingType.BUTTON) {
      await dispatch(clearButtonData())
      navigate(getCreatePath(type))
    } else {
      await dispatch(clearListingCardData())
      await dispatch(clearAutosaveStatus())
      navigate(getCreatePath(type))
    }
  }
  const handleSelectedOption = async (
    option: ColumnMenuOption,
    integrationId: string,
    type?: string
  ) => {
    if (option.key === INTEGRATION_MENU_KEYS.DELETE) {
      setSelectedIntegrationId(integrationId)
      setSelectedIntegrationType(type || null)
      handleOpenDeleteModal()
    } else if (option.key === INTEGRATION_MENU_KEYS.EMBED) {
      setSelectedIntegrationId(integrationId)
      handleOpenModal()
    }
  }

  const typedIntegrations = getIntegrationsByType(integrations, type)
  const buttonEmptyState = getEmptyStateCopy(ListingType.BUTTON)
  const listingEmptyState = getEmptyStateCopy(ListingType.LISTING_CARD)
  const comingSoonEmptyState = getEmptyStateCopy()

  return (
    <>
      <BoxV2
        data-testid="strorefront buttons"
        heading={title}
        actions={
          type ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <ButtonV2
                onClick={navigateToCreate}
                data-testid="create-button"
                styleType="link"
                disabled={false}
                icon={
                  <FontAwesomeIcon
                    icon={faPlus}
                    style={{ width: '13px', height: '14px' }}
                  />
                }
                smallVariant
              >
                Create new {getIntegrationTypeLabel(type)}
              </ButtonV2>
              <VerticalDivider height={'24px'} />
            </div>
          ) : (
            <></>
          )
        }
        variant="small"
        headingVariant="small"
        display={{ open: true }}
        collapsible
        closedSecondaryActions
      >
        {type ? (
          integrations.length > 0 ? (
            typedIntegrations.map((integration, filteredIndex) => (
              <React.Fragment key={integration.id}>
                {filteredIndex > 0 && (
                  <div className={classes.horizontalDevider} />
                )}
                <div
                  onMouseEnter={() => setHoveredRow(integration.id)}
                  onMouseLeave={() => setHoveredRow(null)}
                  style={{ width: '100%' }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      width: '100%',
                    }}
                  >
                    <div className={classes.rowBox}>
                      <div className={classes.rowConatiner}>
                        <img
                          src={
                            type === ListingType.BUTTON ? button : listingCard
                          }
                          alt="Labra"
                        />
                      </div>
                      <Typography className={classes.rowTitle}>
                        {integration.name}
                      </Typography>
                    </div>

                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                      }}
                    >
                      {hoveredRow === integration.id ? (
                        <div style={{ display: 'flex', gap: '12px' }}>
                          <ButtonV2
                            onClick={handleClick?.(integration.id)}
                            data-testid="edit-button"
                            styleType="link"
                            disabled={false}
                            smallVariant
                            icon={
                              <FontAwesomeIcon
                                icon={faPencil}
                                style={{ width: '13px', height: '13px' }}
                              />
                            }
                          >
                            Edit
                          </ButtonV2>
                          <VerticalDivider height="24px" />
                          <div
                            data-prevent-row-click
                            data-testid={'actions'}
                            style={{
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                            }}
                          >
                            <ColumnActionWithMenuV2
                              iconColor={ColumnActionWithMenuColors.BLUE}
                              handleSelectOption={option =>
                                handleSelectedOption(
                                  option,
                                  integration?.id || '',
                                  integration?.type || ''
                                )
                              }
                              menuOptions={[
                                {
                                  key: INTEGRATION_MENU_KEYS.DELETE,
                                  label: 'Delete',
                                },
                                {
                                  key: INTEGRATION_MENU_KEYS.EMBED,
                                  label: 'Get embed code snippet',
                                  disable: isEmbedMenuDisabled(
                                    integration?.cards
                                  ),
                                },
                              ]}
                            />
                          </div>
                        </div>
                      ) : (
                        <>
                          <div style={{ width: '20px', height: '20px' }}>
                            <img
                              src={getCloudImg('AWS')}
                              data-testid={`icon1-${filteredIndex}`}
                              style={{ width: '20px', height: '20px' }}
                              alt="Cloud icon"
                            />
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </React.Fragment>
            ))
          ) : type === ListingType.BUTTON ? (
            <NoDataView
              navigateToCreate={navigateToCreate}
              type={type}
              ButtonText={buttonEmptyState.ButtonText}
              title={buttonEmptyState.title}
              subText={buttonEmptyState.subText}
            />
          ) : (
            <NoDataView
              navigateToCreate={navigateToCreate}
              type={type}
              ButtonText={listingEmptyState.ButtonText}
              title={listingEmptyState.title}
              subText={listingEmptyState.subText}
            />
          )
        ) : (
          <NoDataView
            navigateToCreate={navigateToCreate}
            type={type}
            title={comingSoonEmptyState.title}
            subText={comingSoonEmptyState.subText}
          />
        )}
      </BoxV2>
      <EmbedCodeModal
        integrationId={selectedIntegrationId || ''}
        partnerId={accountId as string}
        open={openlModal}
        onClose={handleCloseModal}
      />
      <DeleteConfirmationModal
        handleDeleteModal={handleDeleteModal}
        open={openDeleteModal}
        onClose={handleCloseDeleteModal}
        selectedIntegrationType={selectedIntegrationType}
      />
    </>
  )
}
